import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import type { RootState } from '../../store';
import {
  HeartIcon,
  ShareIcon,
  CalendarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CheckCircleIcon,
  PlayIcon,
  VideoCameraIcon,
  PhotoIcon,
  MusicalNoteIcon,
  DocumentTextIcon,
  BellIcon,
  ShoppingBagIcon,
  Squares2X2Icon,
  TableCellsIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon, BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';
import Button from '../../components/ui/Button';
import Card, { CardHeader, CardContent } from '../../components/ui/Card';
import Modal, { ModalContent, ModalFooter } from '../../components/ui/Modal';
import { creatorService } from '../../services/creatorService';
import { postsService } from '../../services/postsService';
import { shopService } from '../../services/shopService';
import { livestreamService } from '../../services/livestreamService';
import { followsService } from '../../services/followsService';
import { paymentsService } from '../../services/paymentsService';
import PollDisplay from '../../components/polls/PollDisplay';
import CommentsSection from '../../components/comments/CommentsSection';
import ShareButtons from '../../components/social/ShareButtons';
import VideoPlayer from '../../components/media/VideoPlayer';
import EmojiReactions from '../../components/reactions/EmojiReactions';
import BadgesDisplay from '../../components/badges/BadgesDisplay';
import TierComparison from '../../components/membership/TierComparison';
import { generateCreatorStructuredData } from '../../utils/seo';
import GiftPurchaseModal from '../../components/gifts/GiftPurchaseModal';
import TipButton from '../../components/tips/TipButton';
import ReportModal from '../../components/ui/ReportModal';

interface Creator {
  id: string;
  name: string;
  username: string;
  displayName: string;
  description: string;
  bio: string;
  avatar?: string;
  banner?: string;
  location?: string;
  website?: string;
  isVerified: boolean;
  categories: string[];
  patronCount: number;
  monthlyIncome: number;
  customUrl: string;
  primaryColor: string;
  secondaryColor: string;
  fontColor: string;
  backgroundColor: string;
  socialLinks?: {
    twitter?: string;
    instagram?: string;
    youtube?: string;
    discord?: string;
  };
  tiers: Tier[];
  posts: Post[];
  createdAt: string;
}

interface Tier {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  billingCycle: 'MONTHLY' | 'YEARLY';
  benefits: string[];
  isPublic: boolean;
  currentPatrons: number;
  maxPatrons?: number;
  customButtonText?: string;
  accessUrl?: string;
  sortOrder: number;
  isActive: boolean;
  freeTrialDays?: number;
}

interface Post {
  id: string;
  title?: string;
  content: string;
  contentType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'POLL' | 'LINK';
  mediaUrls: string[];
  isPublic: boolean;
  isScheduled: boolean;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  isLiked?: boolean;
  _count?: {
    likes: number;
    comments: number;
  };
}

const CreatorPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [creator, setCreator] = useState<Creator | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftTier, setGiftTier] = useState<Tier | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; percentOff?: number; amountOff?: number } | null>(null);
  const [activeTab, setActiveTab] = useState<'posts' | 'about' | 'tiers' | 'shop' | 'livestreams'>('posts');
  const [tierViewMode, setTierViewMode] = useState<'grid' | 'comparison'>('grid');
  const [shopProducts, setShopProducts] = useState<any[]>([]);
  const [livestreams, setLivestreams] = useState<any[]>([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: 'POST' | 'COMMENT' | 'USER'; id: string; title?: string } | null>(null);

  useEffect(() => {
    if (username) {
      loadCreator(username);
    }
  }, [username]);

  useEffect(() => {
    // Check follow status when creator is loaded
    if (creator && isAuthenticated && user?.username !== creator.username) {
      checkFollowStatus();
    }
  }, [creator?.id, isAuthenticated, user?.id, creator?.username]);

  const loadCreator = async (creatorUsername: string) => {
    setLoading(true);
    try {
      const res = await creatorService.getPublicCreator(creatorUsername);
      const cp = res.creator;
      const normalized: Creator = {
        id: cp.id,
        name: cp.displayName || cp.user?.username || 'Creator',
        username: cp.user?.username || cp.customUrl || cp.id,
        displayName: cp.displayName || cp.user?.username || 'Creator',
        description: cp.description || '',
        bio: cp.description || '',
        avatar: cp.user?.avatar,
        banner: cp.banner,
        location: cp.user?.location,
        website: cp.user?.website,
        isVerified: !!cp.isVerified,
        categories: cp.category ? [cp.category] : [],
        patronCount: cp._count?.memberships || 0,
        monthlyIncome: cp.analytics?.monthlyRevenue || 0,
        customUrl: cp.customUrl || cp.id,
        primaryColor: cp.primaryColor || '#FF6B6B',
        secondaryColor: cp.secondaryColor || '#4ECDC4',
        fontColor: cp.fontColor || '#1F2937',
        backgroundColor: cp.backgroundColor || '#FFFFFF',
        socialLinks: {
          twitter: cp.twitterHandle,
          instagram: cp.instagramHandle,
          youtube: cp.youtubeUrl,
          discord: cp.discordUrl,
        },
        tiers: (cp.tiers || []).map((tier: any) => ({
          id: tier.id,
          name: tier.name,
          description: tier.description,
          amount: tier.amount,
          currency: tier.currency,
          billingCycle: tier.billingCycle,
          benefits: tier.benefits || [],
          isPublic: true,
          currentPatrons: tier._count?.memberships || tier.currentPatrons || 0,
          maxPatrons: tier.maxPatrons,
          customButtonText: tier.customButtonText,
          accessUrl: tier.accessUrl,
          sortOrder: tier.sortOrder || 0,
          isActive: true,
        })),
        posts: [],
        createdAt: cp.createdAt || new Date().toISOString(),
      };
      setCreator(normalized);

      // Load posts for this creator
      try {
        const postsRes = await postsService.list({
          creatorId: cp.id,
          limit: 20,
        });
        const posts = (postsRes.posts || []).map((p: any) => ({
          id: p.id,
          title: p.title,
          content: p.content,
          contentType: p.contentType,
          mediaUrls: p.mediaUrls || [],
          isPublic: p.isPublic,
          isScheduled: p.isScheduled,
          publishedAt: p.publishedAt,
          createdAt: p.createdAt,
          updatedAt: p.updatedAt,
          isLiked: p.isLiked || false,
          _count: p._count || {},
        }));
        setCreator((prev) => prev ? { ...prev, posts } : null);
      } catch (postError) {
        console.error('Failed to load posts:', postError);
        // Continue without posts
      }

      // Load shop products for this creator
      try {
        const shopRes = await shopService.getProducts(cp.id);
        setShopProducts(shopRes.products || []);
      } catch (shopError) {
        console.error('Failed to load shop products:', shopError);
        // Continue without shop products
      }

      // Load livestreams for this creator
      try {
        const livestreamsRes = await livestreamService.getLivestreams(cp.id);
        setLivestreams(livestreamsRes.livestreams || []);
      } catch (livestreamError) {
        console.error('Failed to load livestreams:', livestreamError);
        // Continue without livestreams
      }
    } catch (error) {
      console.error('Failed to load creator:', error);
      setCreator(null);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTier = (tier: Tier) => {
    setSelectedTier(tier);
    setShowJoinModal(true);
  };

  const handleGiftTier = (tier: Tier) => {
    if (!isAuthenticated) {
      alert('Please log in to purchase a gift');
      return;
    }
    setGiftTier(tier);
    setShowGiftModal(true);
  };

  const handleConfirmJoin = async () => {
    if (!selectedTier || !isAuthenticated) return;

    setIsJoining(true);
    try {
      // Create Stripe Checkout Session and redirect to hosted checkout page
      const response = await paymentsService.createCheckoutSession(selectedTier.id);
      
      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (error: any) {
      console.error('Failed to create checkout session:', error);
      alert(error?.response?.data?.message || 'Failed to start checkout. Please try again.');
      setIsJoining(false);
    }
  };

  const checkFollowStatus = async () => {
    if (!creator?.id || !isAuthenticated) return;
    try {
      const res = await followsService.checkStatus(creator.id, 'CREATOR');
      setIsFollowing(res.isFollowing);
    } catch (error) {
      console.error('Failed to check follow status:', error);
    }
  };

  const handleFollow = async () => {
    if (!creator || !isAuthenticated) {
      alert('Please log in to follow creators');
      return;
    }

    try {
      if (isFollowing) {
        await followsService.unfollow(creator.id, 'CREATOR');
        setIsFollowing(false);
      } else {
        await followsService.follow({ followingId: creator.id, followingType: 'CREATOR' });
        setIsFollowing(true);
      }
    } catch (error: any) {
      console.error('Failed to follow/unfollow creator:', error);
      alert(error?.response?.data?.message || 'Failed to update follow status');
    }
  };

  const handleShare = async () => {
    try {
      const shareData = {
        title: `${creator?.displayName || 'Creator'} on Patreon`,
        text: creator?.description || undefined,
        url: window.location.href,
      };
      if (navigator.share) {
        await navigator.share(shareData as any);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareData.url);
        alert('Link copied to clipboard');
      }
    } catch (e) {
      // ignore
    }
  };

  // Share is now handled by ShareButtons component

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'VIDEO':
        return <VideoCameraIcon className="w-5 h-5" />;
      case 'AUDIO':
        return <MusicalNoteIcon className="w-5 h-5" />;
      case 'IMAGE':
        return <PhotoIcon className="w-5 h-5" />;
      default:
        return <DocumentTextIcon className="w-5 h-5" />;
    }
  };

  const getContentTypeColor = (type: string) => {
    switch (type) {
      case 'VIDEO':
        return 'bg-red-100 text-red-700';
      case 'AUDIO':
        return 'bg-purple-100 text-purple-700';
      case 'IMAGE':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200"></div>
          <div className="max-w-6xl mx-auto px-4 py-8">
            <div className="flex items-start space-x-6">
              <div className="w-24 h-24 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!creator) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Creator Not Found</h1>
          <p className="text-gray-600 mb-4">The creator you're looking for doesn't exist.</p>
          <Link to="/discover">
            <Button>Discover Creators</Button>
          </Link>
        </div>
      </div>
    );
  }

  const publicPosts = creator.posts.filter(post => post.isPublic && !post.isScheduled);
  const publicTiers = creator.tiers.filter(tier => tier.isPublic && tier.isActive);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{creator?.displayName || 'Creator'} | Patreon</title>
        <meta
          name="description"
          content={creator?.description || `Support ${creator?.displayName || 'this creator'} on Patreon. ${creator?.patronCount || 0} patrons already supporting.`}
        />
        <link rel="canonical" href={window.location.origin + `/creator/${creator?.customUrl || creator?.username || creator?.id}`} />
        <meta property="og:title" content={`${creator?.displayName || 'Creator'} | Patreon`} />
        <meta property="og:description" content={creator?.description || `Support ${creator?.displayName || 'this creator'} on Patreon.`} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={window.location.href} />
        {creator?.avatar && <meta property="og:image" content={creator.avatar} />}
        {creator?.banner && <meta property="og:image" content={creator.banner} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${creator?.displayName || 'Creator'} | Patreon`} />
        <meta name="twitter:description" content={creator?.description || `Support ${creator?.displayName || 'this creator'} on Patreon.`} />
        {creator?.avatar && <meta name="twitter:image" content={creator.avatar} />}
        {/* RSS feed */}
        <link
          rel="alternate"
          type="application/rss+xml"
          title={`${creator.displayName} - RSS`}
          href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/rss/creator/${creator.username}`}
        />
        {/* JSON-LD structured data */}
        {creator && (
          <script type="application/ld+json">
            {JSON.stringify(generateCreatorStructuredData({
              id: creator.id,
              displayName: creator.displayName,
              description: creator.description || '',
              avatar: creator.avatar,
              website: creator.website,
              username: creator.username,
              patronCount: creator.patronCount,
              createdAt: creator.createdAt,
            }))}
          </script>
        )}
      </Helmet>
      {/* Hero Section with Custom Styling */}
      <div
        className="relative h-64 bg-cover bg-center"
        style={{
          backgroundColor: creator.backgroundColor,
          backgroundImage: creator.banner ? `url(${creator.banner})` : undefined
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/20"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end space-x-4">
              {creator.avatar ? (
                <img
                  src={creator.avatar}
                  alt={creator.displayName}
                  className="w-24 h-24 rounded-full border-4 border-white object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full border-4 border-white flex items-center justify-center text-white text-2xl font-bold patreon-primary-gradient" style={{ background: 'var(--patreon-btn-primary-bg)' }}>
                  {creator.name.charAt(0)}
                </div>
              )}
              <div className="flex-1 pb-2">
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl font-bold text-white" style={{ color: creator.fontColor }}>
                    {creator.displayName}
                  </h1>
                  {creator.isVerified && (
                    <CheckCircleIcon className="w-6 h-6 text-blue-400" title="Verified Creator" />
                  )}
                </div>
                <p className="text-white/90" style={{ color: creator.fontColor }}>
                  @{creator.username}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Creator Info and Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <p className="text-gray-900 mb-4">{creator.description}</p>

              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                {creator.location && (
                  <span className="flex items-center">
                    <MapPinIcon className="w-4 h-4 mr-1" />
                    {creator.location}
                  </span>
                )}
                <span className="flex items-center">
                  <UserGroupIcon className="w-4 h-4 mr-1" />
                  {creator.patronCount.toLocaleString()} patrons
                </span>
                <span className="flex items-center">
                  <CurrencyDollarIcon className="w-4 h-4 mr-1" />
                  {formatCurrency(creator.monthlyIncome)}/month
                </span>
                <span className="flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Joined {formatDate(creator.createdAt)}
                </span>
              </div>

              {/* Social Links */}
              {creator.socialLinks && (
                <div className="flex items-center space-x-4 mb-4">
                  {creator.socialLinks.twitter && (
                    <a href={`https://twitter.com/${creator.socialLinks.twitter}`} className="text-gray-400 hover:text-gray-600">
                      Twitter
                    </a>
                  )}
                  {creator.socialLinks.instagram && (
                    <a href={`https://instagram.com/${creator.socialLinks.instagram}`} className="text-gray-400 hover:text-gray-600">
                      Instagram
                    </a>
                  )}
                  {creator.socialLinks.youtube && (
                    <a href={`https://youtube.com/${creator.socialLinks.youtube}`} className="text-gray-400 hover:text-gray-600">
                      YouTube
                    </a>
                  )}
                  {creator.socialLinks.discord && (
                    <a href={creator.socialLinks.discord} className="text-gray-400 hover:text-gray-600">
                      Discord
                    </a>
                  )}
                </div>
              )}

              {/* Badges Display */}
              {creator.id && (
                <div className="mb-4">
                  <BadgesDisplay userId={creator.id} maxDisplay={6} />
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {/* Tip button to support creator without subscribing */}
              <TipButton
                toCreatorId={creator.id}
                toCreatorName={creator.displayName}
                className="hidden sm:inline-flex"
              />
              {isAuthenticated && user?.username !== creator.username && (
                <Button
                  variant="outline"
                  onClick={handleFollow}
                  leftIcon={isFollowing ? <BellSolidIcon className="w-4 h-4" /> : <BellIcon className="w-4 h-4" />}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </Button>
              )}

              <Button
                variant="outline"
                onClick={handleShare}
                leftIcon={<ShareIcon className="w-4 h-4" />}
              >
                Share
              </Button>

              {publicTiers.length > 0 && (
                <Button
                  leftIcon={<HeartIcon className="w-4 h-4" />}
                  onClick={() => handleJoinTier(publicTiers[0])}
                >
                  Become a Patron
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8 overflow-x-auto">
          <nav className="-mb-px flex space-x-8 whitespace-nowrap px-1">
            <button
              onClick={() => setActiveTab('posts')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'posts'
                  ? 'text-[color:var(--patreon-orange)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              style={activeTab === 'posts' ? { borderBottomColor: 'var(--patreon-orange)' } : undefined}
            >
              Posts ({publicPosts.length})
            </button>
            <button
              onClick={() => setActiveTab('about')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'about'
                  ? 'text-[color:var(--patreon-orange)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              style={activeTab === 'about' ? { borderBottomColor: 'var(--patreon-orange)' } : undefined}
            >
              About
            </button>
            <button
              onClick={() => setActiveTab('tiers')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'tiers'
                  ? 'text-[color:var(--patreon-orange)]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              style={activeTab === 'tiers' ? { borderBottomColor: 'var(--patreon-orange)' } : undefined}
            >
              Membership ({publicTiers.length})
            </button>
            {shopProducts.length > 0 && (
              <button
                onClick={() => setActiveTab('shop')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'shop'
                    ? 'text-[color:var(--patreon-orange)]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={activeTab === 'shop' ? { borderBottomColor: 'var(--patreon-orange)' } : undefined}
              >
                Shop ({shopProducts.length})
              </button>
            )}
            {livestreams.length > 0 && (
              <button
                onClick={() => setActiveTab('livestreams')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'livestreams'
                    ? 'text-[color:var(--patreon-orange)]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={activeTab === 'livestreams' ? { borderBottomColor: 'var(--patreon-orange)' } : undefined}
              >
                Livestreams ({livestreams.length})
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'posts' && (
          <div className="space-y-6">
            {publicPosts.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No public posts yet</h3>
                  <p className="text-gray-600">
                    {creator.name} hasn't published any public posts yet. Become a patron to see exclusive content.
                  </p>
                </CardContent>
              </Card>
            ) : (
              publicPosts.map((post) => (
                <Card key={post.id}>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      {/* Post Header */}
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className={`p-2 rounded-lg ${getContentTypeColor(post.contentType)}`}>
                            {getContentTypeIcon(post.contentType)}
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {post.title || 'Untitled Post'}
                          </h3>
                          <div className="flex items-center space-x-6 text-sm text-gray-500 mb-2">
                            <span className="flex items-center">
                              <CalendarIcon className="w-4 h-4 mr-1" />
                              {post.publishedAt && formatDate(post.publishedAt)}
                            </span>
                            <button
                              onClick={async () => {
                                if (!isAuthenticated) {
                                  alert('Please log in to like posts');
                                  return;
                                }
                                try {
                                  const res = await postsService.like(post.id);
                                  // Optimistically update the post
                                  setCreator(prev => prev ? {
                                    ...prev,
                                    posts: prev.posts.map((p) =>
                                      p.id === post.id
                                        ? {
                                            ...p,
                                            isLiked: res.isLiked,
                                            _count: {
                                              likes: res.isLiked
                                                ? (p._count?.likes || 0) + 1
                                                : Math.max(0, (p._count?.likes || 0) - 1),
                                              comments: p._count?.comments || 0,
                                            },
                                          }
                                        : p
                                    ),
                                  } : prev);

                                } catch (error: any) {
                                  console.error('Failed to like post:', error);
                                  alert(error?.response?.data?.message || 'Failed to like post');
                                }
                              }}
                              className={`flex items-center transition-colors ${
                                post.isLiked 
                                  ? 'text-red-600 hover:text-red-700' 
                                  : 'text-gray-500 hover:text-red-600'
                              }`}
                              title={post.isLiked ? 'Unlike' : 'Like'}
                            >
                              {post.isLiked ? (
                                <HeartSolidIcon className="w-4 h-4 mr-1" />
                              ) : (
                                <HeartIcon className="w-4 h-4 mr-1" />
                              )}
                              {post._count?.likes || 0}
                            </button>
                            <button
                              onClick={() => {
                                // Scroll to comments section for this post
                                const commentsElement = document.querySelector(`#comments-${post.id}`);
                                if (commentsElement) {
                                  commentsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  // Focus on comment input if exists
                                  const commentInput = commentsElement.querySelector('textarea');
                                  if (commentInput) {
                                    setTimeout(() => commentInput.focus(), 300);
                                  }
                                }
                              }}
                              className="flex items-center text-gray-500 hover:text-blue-600 transition-colors"
                              title="View comments"
                            >
                              <DocumentTextIcon className="w-4 h-4 mr-1" />
                              {post._count?.comments || 0}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Post Content */}
                      {post.content && (
                        <p className="text-gray-700 leading-relaxed line-clamp-3">{post.content}</p>
                      )}

                      {/* Poll Display */}
                      {post.contentType === 'POLL' && post.mediaUrls && post.mediaUrls.length > 0 && (
                        <div className="mt-4">
                          <PollDisplay
                            postId={post.id}
                            pollOptions={post.mediaUrls}
                            onVote={() => {
                              // Reload posts to update counts if needed
                            }}
                          />
                        </div>
                      )}

                      {/* Media Display */}
                      {post.mediaUrls && post.mediaUrls.length > 0 && post.contentType !== 'POLL' && (
                        <div className="space-y-2">
                          {post.contentType === 'IMAGE' && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {post.mediaUrls.slice(0, 4).map((url, idx) => (
                                <img
                                  key={idx}
                                  src={url}
                                  alt={`${post.title || 'Post'} - Image ${idx + 1}`}
                                  className="w-full h-48 object-cover rounded-lg"
                                />
                              ))}
                            </div>
                          )}
                          {post.contentType === 'VIDEO' && post.mediaUrls.length > 0 && (
                            <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                              <VideoPlayer
                                src={post.mediaUrls[0]}
                                title={post.title}
                                controls={true}
                                className="w-full h-full"
                              />
                            </div>
                          )}
                          {post.contentType === 'AUDIO' && post.mediaUrls.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-4">
                              <audio src={post.mediaUrls[0]} controls className="w-full">
                                Your browser does not support the audio tag.
                              </audio>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Post Actions */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-4">
                          <EmojiReactions
                            targetType="POST"
                            targetId={post.id}
                            currentUserId={user?.id}
                          />
                          <ShareButtons
                            url={`${window.location.origin}/creator/${creator.username}/post/${post.id}`}
                            title={post.title || 'Post'}
                            description={post.content?.substring(0, 100)}
                            image={post.mediaUrls?.[0]}
                          />
                          {isAuthenticated && user?.username !== creator.username && (
                            <button
                              onClick={() => {
                                setReportTarget({ type: 'POST', id: post.id, title: post.title || 'Post' });
                                setShowReportModal(true);
                              }}
                              className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              title="Report post"
                            >
                              <FlagIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Comments Section */}
                      <div id={`comments-${post.id}`}>
                        <CommentsSection
                          postId={post.id}
                          onCommentCountChange={(count) => {
                            // Update comment count in post display if needed
                          }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {activeTab === 'about' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader title="About" children={null} />
                <CardContent>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed">{creator.bio}</p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Recent Achievements</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 107, 53, 0.1)' }}>
                          <CheckCircleIcon className="w-5 h-5" style={{ color: 'var(--patreon-orange)' }} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">1000+ Patrons Milestone</p>
                          <p className="text-sm text-gray-600">Reached in January 2024</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Verified Creator</p>
                          <p className="text-sm text-gray-600">Verified since March 2023</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card>
                <CardHeader title="Categories" children={null} />
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {creator.categories.map((category) => (
                      <span
                        key={category}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'tiers' && (
          <div className="space-y-6">
            {publicTiers.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <CurrencyDollarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No membership tiers available</h3>
                  <p className="text-gray-600">
                    {creator.name} hasn't set up any membership tiers yet.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* View Toggle */}
                {publicTiers.length > 1 && (
                  <div className="flex justify-end mb-6">
                    <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setTierViewMode('grid')}
                        className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${
                          tierViewMode === 'grid'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Squares2X2Icon className="w-5 h-5" />
                        <span className="text-sm font-medium">Grid View</span>
                      </button>
                      <button
                        onClick={() => setTierViewMode('comparison')}
                        className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${
                          tierViewMode === 'comparison'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <TableCellsIcon className="w-5 h-5" />
                        <span className="text-sm font-medium">Compare</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Grid View */}
                {tierViewMode === 'grid' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {publicTiers.map((tier, index) => (
                  <motion.div
                    key={tier.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card className="relative overflow-hidden hover:shadow-2xl hover:scale-105 transition-all duration-300">
                      {tier.maxPatrons && tier.currentPatrons >= tier.maxPatrons && (
                      <div className="absolute inset-0 bg-gray-900/80 z-10 flex items-center justify-center">
                        <div className="text-center text-white">
                          <p className="text-lg font-semibold">Sold Out</p>
                          <p className="text-sm">This tier is currently full</p>
                        </div>
                      </div>
                    )}

                    <CardContent className="p-6">
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                        <div className="text-3xl font-bold text-gray-900 mb-1">
                          {formatCurrency(tier.amount)}
                        </div>
                        <p className="text-gray-600">per {tier.billingCycle === 'MONTHLY' ? 'month' : 'year'}</p>
                        {tier.freeTrialDays && tier.freeTrialDays > 0 && (
                          <div className="mt-3">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                              🎁 {tier.freeTrialDays}-day free trial
                            </span>
                          </div>
                        )}
                      </div>

                      <p className="text-gray-600 mb-6">{tier.description}</p>

                      <div className="space-y-2 mb-6">
                        {tier.benefits.map((benefit, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{benefit}</span>
                          </div>
                        ))}
                      </div>

                      {tier.maxPatrons && (
                        <div className="mb-4 text-center">
                          <p className="text-sm text-gray-600">
                            {tier.maxPatrons - tier.currentPatrons} spots remaining
                          </p>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                            <div
                              className="h-2 rounded-full transition-all duration-300"
                              style={{ backgroundColor: 'var(--patreon-orange)', width: `${(tier.currentPatrons / tier.maxPatrons) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      <div className="text-center text-sm text-gray-600 mb-4">
                        {tier.currentPatrons} patrons
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Button
                          className="w-full"
                          onClick={() => handleJoinTier(tier)}
                          disabled={!!(tier.maxPatrons && tier.currentPatrons >= tier.maxPatrons)}
                        >
                          {tier.customButtonText || `Choose ${tier.name}`}
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => handleGiftTier(tier)}
                        >
                          Gift this tier
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
                ))}
                </div>
              )}

                {/* Comparison View */}
                {tierViewMode === 'comparison' && (
                  <TierComparison
                    tiers={publicTiers}
                    onSelectTier={handleJoinTier}
                  />
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'shop' && (
              <div className="space-y-6">
                {shopProducts.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <ShoppingBagIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No products available</h3>
                      <p className="text-gray-600">
                        {creator.name} hasn't added any products to their shop yet.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {shopProducts.map((product: any) => (
                      <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                        <div className="relative">
                          {product.imageUrl ? (
                            <img
                              src={product.imageUrl}
                              alt={product.title}
                              className="w-full h-48 object-cover"
                            />
                          ) : (
                            <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                              <PhotoIcon className="w-12 h-12 text-gray-400" />
                            </div>
                          )}
                          <div className="absolute top-2 right-2">
                            <span className="px-2 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700">
                              {product.productType === 'DIGITAL' ? 'Digital' : product.productType === 'PHYSICAL' ? 'Physical' : 'Service'}
                            </span>
                          </div>
                        </div>
                        <CardContent className="p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.title}</h3>
                          {product.description && (
                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                          )}
                          <div className="flex items-center justify-between mb-4">
                            <p className="text-2xl font-bold text-gray-900">{formatCurrency(product.price, product.currency)}</p>
                            {product.productType === 'PHYSICAL' && product.inventory !== null && (
                              <p className="text-sm text-gray-600">
                                {product.inventory} in stock
                              </p>
                            )}
                          </div>
                          <Button
                            className="w-full"
                            onClick={async () => {
                              if (!isAuthenticated) {
                                alert('Please log in to purchase products');
                                return;
                              }
                              try {
                                const res = await shopService.purchaseProduct(product.id);
                                if (res.downloadUrl) {
                                  window.open(res.downloadUrl, '_blank');
                                }
                                alert('Product purchased successfully!');
                                // Reload to update purchase status
                                await loadCreator(username!);
                              } catch (error: any) {
                                alert(error?.response?.data?.message || 'Failed to purchase product');
                              }
                            }}
                          >
                            {product.productType === 'DIGITAL' ? 'Purchase & Download' : 'Purchase'}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'livestreams' && (
              <div className="space-y-6">
                {livestreams.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-12">
                      <VideoCameraIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No livestreams available</h3>
                      <p className="text-gray-600">
                        {creator.name} hasn't scheduled any livestreams yet.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {livestreams.map((livestream: any) => (
                      <Card key={livestream.id} className="overflow-hidden">
                        <div className="flex items-start space-x-6">
                          <div className="flex-shrink-0">
                            {livestream.thumbnailUrl ? (
                              <img
                                src={livestream.thumbnailUrl}
                                alt={livestream.title}
                                className="w-48 h-32 object-cover rounded-lg"
                              />
                            ) : (
                              <div className="w-48 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                                <VideoCameraIcon className="w-12 h-12 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <CardContent className="p-6 flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">{livestream.title}</h3>
                                <div className="flex items-center space-x-4 mb-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    livestream.status === 'LIVE' ? 'bg-red-100 text-red-700' :
                                    livestream.status === 'SCHEDULED' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {livestream.status === 'LIVE' && (
                                      <span className="w-2 h-2 bg-red-600 rounded-full inline-block mr-1 animate-pulse"></span>
                                    )}
                                    {livestream.status}
                                  </span>
                                  {livestream.status === 'LIVE' && (
                                    <span className="text-sm text-gray-600">
                                      {livestream.viewerCount} viewers
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {livestream.description && (
                              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{livestream.description}</p>
                            )}

                            <div className="flex items-center space-x-6 text-sm text-gray-600 mb-4">
                              {livestream.scheduledStartAt && (
                                <span className="flex items-center">
                                  <CalendarIcon className="w-4 h-4 mr-1" />
                                  {formatDate(livestream.scheduledStartAt)}
                                </span>
                              )}
                              {livestream.status === 'LIVE' && livestream.startedAt && (
                                <span className="flex items-center text-red-600">
                                  <PlayIcon className="w-4 h-4 mr-1" />
                                  Live now
                                </span>
                              )}
                            </div>

                            {livestream.status === 'LIVE' && livestream.playbackUrl && (
                              <Button
                                className="w-full"
                                onClick={() => window.open(livestream.playbackUrl, '_blank')}
                                leftIcon={<PlayIcon className="w-4 h-4" />}
                              >
                                Watch Live Stream
                              </Button>
                            )}
                            {livestream.status === 'SCHEDULED' && (
                              <Button
                                variant="outline"
                                className="w-full"
                                disabled
                              >
                                Scheduled for {formatDate(livestream.scheduledStartAt)}
                              </Button>
                            )}
                            {livestream.status === 'ENDED' && (
                              <Button
                                variant="outline"
                                className="w-full"
                                disabled
                              >
                                Stream Ended
                              </Button>
                            )}
                          </CardContent>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
      </div>

      {/* Join Modal */}
      <Modal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        title={`Join ${creator.displayName}`}
        size="md"
      >
        <ModalContent>
          <div className="space-y-6">
            <div className="text-center">
              {creator.avatar ? (
                <img
                  src={creator.avatar}
                  alt={creator.displayName}
                  className="w-16 h-16 rounded-full mx-auto mb-4 object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4 patreon-primary-gradient" style={{ background: 'var(--patreon-btn-primary-bg)' }}>
                  {creator.name.charAt(0)}
                </div>
              )}
              <div className="mb-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedTier?.name}
                </h3>
                {selectedTier?.freeTrialDays && selectedTier.freeTrialDays > 0 && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                      🎁 {selectedTier.freeTrialDays}-day free trial
                    </span>
                  </div>
                )}
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-2">
                {selectedTier && formatCurrency(selectedTier.amount)}/month
              </p>
              <p className="text-gray-600">{selectedTier?.description}</p>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">You'll get:</h4>
              <ul className="space-y-2">
                {selectedTier?.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {isAuthenticated && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Have a coupon code?</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setAppliedCoupon(null);
                    }}
                    placeholder="Enter coupon code"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                {appliedCoupon && (
                  <p className="mt-2 text-sm text-green-600">
                    Coupon {appliedCoupon.code} applied!
                    {appliedCoupon.percentOff ? ` ${appliedCoupon.percentOff}% off` : ''}
                    {appliedCoupon.amountOff ? ` ${appliedCoupon.amountOff} off` : ''}
                  </p>
                )}
              </div>
            )}

            {!isAuthenticated && (
              <div className="rounded-lg p-4" style={{ backgroundColor: 'rgba(255, 107, 53, 0.05)', borderColor: 'rgba(255, 107, 53, 0.2)', borderWidth: '1px', borderStyle: 'solid' }}>
                <p className="text-sm" style={{ color: 'var(--patreon-orange-darker)' }}>
                  You need to be logged in to become a patron. Please log in or create an account to continue.
                </p>
              </div>
            )}
          </div>
        </ModalContent>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setShowJoinModal(false)}
            disabled={isJoining}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmJoin}
            loading={isJoining}
            disabled={!isAuthenticated}
          >
            {!isAuthenticated ? 'Log in to Continue' : 'Confirm Membership'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Gift Purchase Modal */}
      <GiftPurchaseModal
        isOpen={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        tier={giftTier ? { id: giftTier.id, name: giftTier.name, amount: giftTier.amount, currency: giftTier.currency } : null}
        creator={{ displayName: creator.displayName, id: creator.id }}
      />

      {/* Report Modal */}
      {showReportModal && reportTarget && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setReportTarget(null);
          }}
          targetType={reportTarget.type}
          targetId={reportTarget.id}
          targetTitle={reportTarget.title}
          onReportSubmitted={() => {
            // Could add a toast notification here
          }}
        />
      )}
    </div>
  );
};

export default CreatorPage;
